import { type FileAsset, FileType, type FolderAsset } from 'models';
import { Form, FormActions, Input, Modal, type ModalOptions, message } from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import { useUpdateAssetMutation } from '../../queries';

export type AssetsRenameModalOptions = {
  editingFile: FileAsset | FolderAsset;
};

type AssetsRenameModalFormValues = {
  name: string;
};

type P = {
  onCancel: () => void;
  options: ModalOptions<AssetsRenameModalOptions>;
};

const AssetsRenameModal = ({ onCancel, options }: P) => {
  const { isPending: isLoadingRenameFile, mutate: renameFile } = useUpdateAssetMutation();

  const handleFormFinish = (values: AssetsRenameModalFormValues) => {
    if (!options.isOpened) {
      return;
    }

    const isFile = options.editingFile.type === FileType;

    renameFile(
      {
        id: options.editingFile.id,
        name: values.name,
      },
      {
        onError(error) {
          handleAxiosError(
            error,
            isFile
              ? "Something went wrong. File couldn't be renamed."
              : "Something went wrong. Folder couldn't be renamed.",
          );
        },
        onSuccess() {
          message.success(isFile ? 'File renamed successfully' : 'Folder renamed successfully');
          onCancel();
        },
      },
    );
  };

  const isFile = options.isOpened && options.editingFile.type === FileType;

  return (
    <Modal isOpened={options.isOpened} onCancel={onCancel} title="Rename file" width="medium">
      {options.isOpened ? (
        <Modal.HugContent>
          <Form<AssetsRenameModalFormValues>
            initialValues={{ name: options.editingFile.name }}
            onFinish={handleFormFinish}
          >
            <Form.Item<AssetsRenameModalFormValues>
              label={isFile ? 'File name' : 'Folder name'}
              name="name"
              rules={[{ required: true, whitespace: true }]}
            >
              <Input />
            </Form.Item>
            <Modal.HugContent.Footer>
              <FormActions
                right={{ children: 'Cancel', onClick: onCancel }}
                submit={{ children: 'Save', loading: isLoadingRenameFile }}
              />
            </Modal.HugContent.Footer>
          </Form>
        </Modal.HugContent>
      ) : null}
    </Modal>
  );
};

export default AssetsRenameModal;
